import React, { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../index.css";
import { useTranslation } from "react-i18next";
import HeidiLogo from "../assets/HEIDI_Logo_Black.png";
import { resetPassword, login, googleLogin, appleLogin } from "../Services/usersApi";
import Alert from "../Components/Alert";

const LoginPage = () => {
	const { t } = useTranslation();
	const TIMEOUT_DURATION = 5000;
	const userRef = useRef();
	const [rememberMe, setRememberMe] = useState(false);
	const [forgotPassword, setForgotPassword] = useState(false);
	const [mailNotRecieved, setMailNotRecieved] = useState(false);
	const [sendEmail, setSendEmail] = useState(false);
	const [alertInfo, setAlertInfo] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");
	const [alertType, setAlertType] = useState("");
	const [user, setUser] = useState("");
	const [userReset, setUserReset] = useState("");
	const [pwd, setPwd] = useState("");
	const [loginLoading, setLoginLoading] = useState("");
	const [forgotPasswordLoading, setForgotPasswordLoading] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const navigate = useNavigate();
	const navigateTo = (path) => {
		if (path) {
			navigate(path);
		}
	};

	const routeChangeToUpload = useCallback(() => {
		const path = `/UploadListings`;
		navigate(path);
	}, [navigate]);

	const routeChangeToRegister = () => {
		const path = `/Register`;
		navigate(path);
	};

	const location = useLocation();
	const [timeOutAlertMessage, settimeOutAlertMessage] = useState("");
	useEffect(() => {
		document.title = process.env.REACT_APP_REGION_NAME;
		const searchParams = new URLSearchParams(location.search);

		userRef.current.focus();
		const accessToken =
			window.localStorage.getItem("accessToken") ||
			window.sessionStorage.getItem("accessToken");
		const refreshToken =
			window.localStorage.getItem("refreshToken") ||
			window.sessionStorage.getItem("refreshToken");
		if (accessToken?.length === 456 || refreshToken?.length === 456) {
			routeChangeToUpload();
		}

		if (searchParams.get("sessionExpired") === "true") {
			settimeOutAlertMessage(t("sessionExpired"));
			setAlertType("danger");
			setTimeout(() => {
				settimeOutAlertMessage("");
			}, TIMEOUT_DURATION);
		}
	}, [routeChangeToUpload, location]);

	const handlePasswordChange = (event) => {
		setPwd(event.target.value);
	};

	const toggleShowPassword = () => {
		setShowPassword(!showPassword);
	};

	const onCancel = () => {
		setForgotPassword(false);
		setSendEmail(false);
		setMailNotRecieved(false);
		setAlertInfo(false);
		setUserReset("");
		setAlertMessage("");
		setRememberMe(false);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();
		setLoginLoading(true);

		try {
			const response = await login({ email: user, password: pwd, rememberMe });
			const userData = response.data;

			if (userData.status === "error" && userData.errorCode === 8500) {
				console.warn("Login] User not found in backend. Redirecting to /WelcomePage");
				navigateTo("/WelcomePage");
				return;
			}

			navigateTo("/");
		} catch (backendError) {
			let errorMessage = t("invalidCredentials");

			const errorCode = backendError.response?.data?.errorCode;
			const firebaseCode = backendError.code;

			if (errorCode === 8500) {
				navigateTo("/WelcomePage");
				return;
			}

			if (errorCode === 2006) {
				errorMessage = t("emailNotVerified");
			} else if (firebaseCode === "auth/user-not-found") {
				errorMessage = t("noAccountFound");
			} else if (firebaseCode === "auth/wrong-password") {
				errorMessage = t("wrongPassword");
			} else if (firebaseCode === "auth/too-many-requests") {
				errorMessage = t("tooManyAttempts");
			}
			else if (errorCode === 8511) {
				errorMessage = t("user_inactive")||"User is inactive";
				alert(errorMessage);
				
			}

			setAlertInfo(true);
			setAlertType("danger");
			setAlertMessage(errorMessage);
		}
		finally {
			setLoginLoading(false); // ✅ Always runs
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter") {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	const handlePasswordReset = async () => {
		setForgotPasswordLoading(true);
		try {
			await resetPassword(userReset);
			setAlertInfo(true);
			setAlertType("success");
			setAlertMessage(t("passwordResetLinkSent"));
		} catch (error) {
			setAlertInfo(true);
			setAlertType("danger");
			setAlertMessage(t("passwordResetFailed"));
		} finally {
			setForgotPasswordLoading(false);
		}
	};

	const handleGoogleLogin = async () => {
		try {
			const response = await googleLogin();
			const userData = response.data;

			if (userData.status === "error" && userData.errorCode === 8100) {
				console.warn("[Login] User not found (8100). Redirecting to /WelcomePage");
				navigate("/WelcomePage");
				return;
			}
			navigate("/");
		} catch (error) {
			let errorMessage = t("googleLoginFailed");
			if (error.code === "auth/popup-closed-by-user") {
				errorMessage = t("popupClosed");
			} else if (error.code === "auth/cancelled-popup-request") {
				errorMessage = t("multiplePopupsBlocked");
			}

			setAlertInfo(true);
			setAlertType("danger");
			setAlertMessage(errorMessage);
		}
	};

	const handleAppleLogin = async () => {
		try {
			const { data: userData, isAppleLoginSuccess } = await appleLogin();

			if (!isAppleLoginSuccess) {
				setAlertInfo(true);
				setAlertType("danger");
				setAlertMessage(t("appleLoginFailed"));
				return;
			}

			if (userData.status === "error" && userData.errorCode === 8100) {
				navigate("/WelcomePage");
				return;
			}
			navigate("/");
		} catch (error) {
			setAlertInfo(true);
			setAlertType("danger");
			setAlertMessage(t("appleLoginFailed"));
		}
	};

	const openModal = () => {
		setIsOpen(true);
	};

	const closeModal = () => {
		setIsOpen(false);
	};

	return (
		<div className="h-screen w-screen bg-white flex justify-center items-center">
			<div className="grid gap-8">
				<div
					className="rounded-[20px] bg-white shadow-[0_3px_10px_rgb(0,0,0,0.2)] p-10 m-2"
				>
					<div>
						<img
							onClick={() => navigate("/")}
							className="mx-auto h-20 w-auto cursor-pointer"
							src={HeidiLogo}
							alt="HEDI- Heimat Digital"
						/>
						<h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-slate-800">
							{t("signIntoAccount")}
						</h2>
					</div>

					<div className="mt-8 space-y-6">
						<input type="hidden" name="remember" value="true" />
						<div className="space-y-2 rounded-md shadow-sm">
							<div>
								<label htmlFor="username" className="sr-only">
									{t("email")}
								</label>
								<input
									type="text"
									id="username"
									ref={userRef}
									name="username"
									value={user}
									autoComplete="on"
									onChange={(e) => setUser(e.target.value)}
									onKeyDown={handleKeyDown}
									required
									className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
									placeholder={t("usernameOrEmail") + " *"}
								/>
							</div>
							<div className="relative flex items-center">
								<label htmlFor="password" className="sr-only">
									{t("password")}
								</label>
								<input
									type={showPassword ? "text" : "password"}
									id="password"
									name="password"
									value={pwd}
									onChange={handlePasswordChange}
									onKeyDown={handleKeyDown}
									required
									className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
									placeholder={t("pleaseEnterPassword") + " *"}
									style={{ paddingRight: '3rem' }}
								/>
								<button
									type="button"
									className="absolute right-0 mr-3 flex font-bold items-center text-sm leading-5 text-gray-500 hover:text-gray-700 focus:outline-none"
									onClick={toggleShowPassword}
									style={{ zIndex: 20 }}
								>
									{showPassword ?
										<svg className="h-6 text-black" fill="none" xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 640 512">
											<path fill="currentColor"
												d="M320 400c-75.85 0-137.25-58.71-142.9-133.11L72.2 185.82c-13.79 17.3-26.48 35.59-36.72 55.59a32.35 32.35 0 0 0 0 29.19C89.71 376.41 197.07 448 320 448c26.91 0 52.87-4 77.89-10.46L346 397.39a144.13 144.13 0 0 1-26 2.61zm313.82 58.1l-110.55-85.44a331.25 331.25 0 0 0 81.25-102.07 32.35 32.35 0 0 0 0-29.19C550.29 135.59 442.93 64 320 64a308.15 308.15 0 0 0-147.32 37.7L45.46 3.37A16 16 0 0 0 23 6.18L3.37 31.45A16 16 0 0 0 6.18 53.9l588.36 454.73a16 16 0 0 0 22.46-2.81l19.64-25.27a16 16 0 0 0-2.82-22.45zm-183.72-142l-39.3-30.38A94.75 94.75 0 0 0 416 256a94.76 94.76 0 0 0-121.31-92.21A47.65 47.65 0 0 1 304 192a46.64 46.64 0 0 1-1.54 10l-73.61-56.89A142.31 142.31 0 0 1 320 112a143.92 143.92 0 0 1 144 144c0 21.63-5.29 41.79-13.9 60.11z">
											</path>
										</svg> :
										<svg className="h-6 text-black" fill="none" xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 576 512">
											<path fill="currentColor"
												d="M572.52 241.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48 241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0 0-29.19zM288 400a144 144 0 1 1 144-144 143.93 143.93 0 0 1-144 144zm0-240a95.31 95.31 0 0 0-25.31 3.79 47.85 47.85 0 0 1-66.9 66.9A95.78 95.78 0 1 0 288 160z">
											</path>
										</svg>
									}
									{/* {showPassword ? t("hide") : t("show")} */}
								</button>
							</div>
						</div>

						<div className="flex items-center justify-between">
							<div className="flex items-center">
								<input
									id="remember-me"
									name="remember-me"
									type="checkbox"
									className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
									defaultChecked={rememberMe}
									onChange={() => {
										setRememberMe(!rememberMe);
									}}
								/>
								<label
									htmlFor="remember-me"
									className="ml-2 block text-sm text-slate-800"
								>
									{t("rememberMe")}
								</label>
							</div>

							<div className="text-sm">
								<span
									onClick={() => setForgotPassword(true)}
									className="font-medium cursor-pointer text-slate-800 hover:text-slate-600"
								>
									{t("forgotYourPassword")}
								</span>
							</div>
						</div>

						<div>
							<button
								onClick={handleSubmit}
								type="button"
								value="Submit"
								id="finalbutton"
								disabled={loginLoading}
								className="bg-gradient-to-r font-md text-gray-300 from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full hover:scale-105 hover:from-gray-700 hover:to-slate-600 transition duration-300 ease-in-out"
							>
								{t("signIn")}
								{loginLoading && (
									<svg
										aria-hidden="true"
										className="inline w-5 h-5 ml-2 text-gray-200 animate-spin fill-gray-600"
										viewBox="0 0 100 101"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
											fill="currentColor"
										/>
										<path
											d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
											fill="currentFill"
										/>
									</svg>
								)}
							</button>
						</div>
						{alertInfo && (
							<div className="py-2 mt-1 px-2">
								<Alert type={alertType} message={alertMessage} />
							</div>
						)}
						{timeOutAlertMessage && (
							<div className="py-2 mt-1 px-2">
								<Alert type={alertType} message={timeOutAlertMessage} />
							</div>
						)}
						{mailNotRecieved && (<div className="flex justify-between">
							<div className="text-sm">
								{t("resendEmailVerification")}
								<span
									onClick={() => {
										setSendEmail(true);
										setForgotPassword(false);
									}}
									className="font-medium cursor-pointer text-black hover:text-blue-400"
								>
									{t("click_here")}
								</span>
							</div>
						</div>)}
						<div className="flex justify-between">
							<div className="text-sm">
								{t("notMember")}
								<span
									onClick={routeChangeToRegister}
									className="font-medium cursor-pointer text-slate-800 hover:text-slate-600"
								>
									{" "}
									{t("register")}
								</span>
							</div>

							<div className="flex cursor-pointer">
								<span
									onClick={openModal}
									className="font-medium cursor-pointer text-slate-800 hover:text-slate-600"
								>
									{t("Help")}
								</span>

								{isOpen && (
									<div className="fixed inset-0 flex items-center justify-center z-50">
										<div className="modal-overlay absolute w-full h-full bg-gray-900 opacity-50"></div>

										<div className="modal-container bg-white w-11/12 md:max-w-md mx-auto rounded-lg shadow-lg z-50 overflow-y-auto">
											<div className="modal-content py-4 text-left px-6">
												<div>
													<h2 className="font-bold text-blue-600 text-xl text-center mb-6">
														{t("anleitung")}
													</h2>
													<h3 className="font-bold text-blue-600 text-lg text-center mb-6">
														{t("subtitle")}
													</h3>
													<p className="mb-6 text-gray-800" dangerouslySetInnerHTML={{ __html: t("step1") }}></p>
													<p className="mb-6 text-gray-800" dangerouslySetInnerHTML={{ __html: t("step2") }}></p>
													<p className="mb-6 text-gray-800" dangerouslySetInnerHTML={{ __html: t("step3") }}></p>
													<p className="mb-6 text-gray-800" dangerouslySetInnerHTML={{ __html: t("step4") }}></p>
													<p className="font-bold text-blue-600 text-lg text-center mb-6">
														<strong>{t("thankYou")}</strong>
													</p>
												</div>

												<div className="mt-6 text-center">
													<button
														onClick={closeModal}
														className="bg-gradient-to-r font-md text-gray-300 from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full hover:scale-105 hover:from-gray-700 hover:to-slate-600 transition duration-300 ease-in-out"
													>
														{t("cancel")}
													</button>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>

					<div className="mt-6">
						<div className="relative">
							<div className="absolute inset-0 flex items-center">
								<div className="w-full border-t border-gray-300"></div>
							</div>
							<div className="relative flex justify-center text-sm">
								<span className="px-2 bg-gray-100 font-medium cursor-pointer text-slate-800 hover:text-slate-600 rounded-lg">
									{t("orContinueWith")}
								</span>
							</div>
						</div>

						<div id="third-party-auth" className="flex items-center justify-center mt-5 flex-wrap">
							<button
								onClick={handleGoogleLogin}
								className="hover:scale-105 ease-in-out duration-300 shadow-lg p-2 rounded-lg m-1"
							>
								<img className="max-w-[25px]" src="https://ucarecdn.com/8f25a2ba-bdcf-4ff1-b596-088f330416ef/" alt="Google" />
							</button>

							<button
								onClick={handleAppleLogin}
								className="hover:scale-105 ease-in-out duration-300 shadow-lg p-2 rounded-lg m-1"
							>
								<img className="max-w-[25px]" src="https://ucarecdn.com/3277d952-8e21-4aad-a2b7-d484dad531fb/" alt="Apple" />
							</button>
						</div>
					</div>

					{(forgotPassword || sendEmail) && (
						<div className="space-y-4">
							<label htmlFor="username" className="sr-only">
								{t("email")}
							</label>
							<input
								type="text"
								id="username"
								name="username"
								value={userReset}
								onChange={(e) => setUserReset(e.target.value)}
								required
								className="border p-3 bg-white text-gray-800 border-gray-700 shadow-md placeholder:text-base duration-300 border-gray-300 rounded-lg w-full"
								placeholder={
									forgotPassword ? t("usernameOrEmail") : t("pleaseEnterEmailAddress") + "*"
								}
							/>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={handlePasswordReset}
									disabled={forgotPasswordLoading}
									className="bg-gradient-to-r from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full hover:scale-105 transition duration-300 ease-in-out"
								>
									{t("sendLink")}
								</button>
								<button
									type="button"
									onClick={onCancel}
									disabled={forgotPasswordLoading}
									className="bg-gradient-to-r from-gray-700 to-slate-600 shadow-lg p-2 text-white rounded-lg w-full hover:scale-105 transition duration-300 ease-in-out"
								>
									{t("cancel")}
								</button>
							</div>
						</div>
					)}
				</div>
			</div >
		</div >
	);
};
export default LoginPage;
